import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormatting } from '../hooks/useFormatting';
import { PoliticianData, TimeframeData } from '../types';
import { generateSlug } from './SEOHead';
import './News.css';

interface NewsArticle {
  id: string;
  slug: string;
  weekStart: string;
  weekEnd: string;
  title: {
    en: string;
    'zh-CN': string;
    'zh-TW': string;
  };
  summary: {
    en: string;
    'zh-CN': string;
    'zh-TW': string;
  };
  content: {
    en: string;
    'zh-CN': string;
    'zh-TW': string;
  };
  highlights: {
    topGainer: {
      name: string;
      party: string;
      return: number;
    };
    topLoser: {
      name: string;
      party: string;
      return: number;
    };
    totalTrades: number;
    mostTradedStocks: string[];
    sectorTrends: {
      sector: string;
      trend: 'bullish' | 'bearish' | 'neutral';
    }[];
  };
  generatedAt: string;
}

interface NewsData {
  articles: NewsArticle[];
  lastUpdated: string;
}

interface Disclosure {
  politician: string;
  party: string;
  chamber: string;
  state: string;
  pub_date: string;
  trade_date: string;
  issuer: string;
  ticker: string;
  owner: string;
  trade_type: string;
  value_range: string;
}

interface DisclosuresData {
  disclosures: Disclosure[];
  lastUpdated: string;
  totalCount: number;
}

interface PoliticianTrades {
  politician: PoliticianData | null;
  politicianName: string;
  party: string;
  chamber: string;
  state: string;
  trades: Disclosure[];
  return3Y: number;
  currentValue: number;
}

const NewsListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatDate } = useFormatting();
  const [newsData, setNewsData] = useState<NewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentLang = i18n.language as 'en' | 'zh-CN' | 'zh-TW';

  useEffect(() => {
    fetch('/data/news/weekly-summaries.json')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load news');
        return res.json();
      })
      .then((data: NewsData) => {
        setNewsData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load news:', err);
        setError(t('news:loadError'));
        setLoading(false);
      });
  }, [t]);

  if (loading) {
    return (
      <div className="news-page loading">
        <div className="loader">
          <div className="loader-text">{t('loading')}</div>
          <div className="loader-bar">
            <div className="loader-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !newsData) {
    return (
      <div className="news-page">
        <header className="news-header">
          <div className="news-header-left">
            <h1 className="news-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              {t('appTitle')}
            </h1>
            <h2 className="news-subtitle">{t('news:title')}</h2>
          </div>
        </header>
        <main className="news-main">
          <div className="news-empty">
            <div className="empty-icon">&#128240;</div>
            <p>{t('news:noArticles')}</p>
            <button className="back-btn" onClick={() => navigate('/')}>
              {t('news:backToDashboard')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="news-page">
      <header className="news-header">
        <div className="news-header-left">
          <h1 className="news-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            {t('appTitle')}
          </h1>
          <h2 className="news-subtitle">{t('news:title')}</h2>
        </div>
      </header>

      <main className="news-main">
        <div className="news-list">
          {newsData.articles.map((article) => (
            <article
              key={article.id}
              className="news-card"
              onClick={() => navigate(`/news/${article.slug}`)}
            >
              <div className="news-card-date">
                <span className="date-range">
                  {formatDate(article.weekStart, 'MMM d')} - {formatDate(article.weekEnd, 'MMM d, yyyy')}
                </span>
              </div>
              <h3 className="news-card-title">
                {article.title[currentLang] || article.title.en}
              </h3>
              <p className="news-card-summary">
                {article.summary[currentLang] || article.summary.en}
              </p>
              <div className="news-card-highlights">
                <div className="highlight-item top">
                  <span className="highlight-icon">&#9650;</span>
                  <span className="highlight-text">
                    {article.highlights.topGainer.name}: +{article.highlights.topGainer.return.toFixed(1)}%
                  </span>
                </div>
                <div className="highlight-item bottom">
                  <span className="highlight-icon">&#9660;</span>
                  <span className="highlight-text">
                    {article.highlights.topLoser.name}: {article.highlights.topLoser.return.toFixed(1)}%
                  </span>
                </div>
                <div className="highlight-item trades">
                  <span className="highlight-icon">&#128202;</span>
                  <span className="highlight-text">
                    {article.highlights.totalTrades} {t('news:trades')}
                  </span>
                </div>
              </div>
              <div className="news-card-footer">
                <span className="read-more">{t('news:readMore')} &rarr;</span>
              </div>
            </article>
          ))}
        </div>
      </main>

      <footer className="news-footer">
        <button className="back-to-dashboard" onClick={() => navigate('/')}>
          {t('news:backToDashboard')}
        </button>
      </footer>
    </div>
  );
};

const NewsArticlePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { formatDate, formatPercentage, formatCurrency } = useFormatting();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [timeseriesData, setTimeseriesData] = useState<TimeframeData | null>(null);
  const [disclosuresData, setDisclosuresData] = useState<DisclosuresData | null>(null);
  const [loading, setLoading] = useState(true);

  const currentLang = i18n.language as 'en' | 'zh-CN' | 'zh-TW';

  useEffect(() => {
    Promise.all([
      fetch('/data/news/weekly-summaries.json').then(res => res.json()),
      fetch('/data/timeseries/1Y.json').then(res => res.json()),
      fetch('/data/disclosures.json').then(res => res.json())
    ])
      .then(([newsData, tsData, discData]: [NewsData, TimeframeData, DisclosuresData]) => {
        const found = newsData.articles.find(a => a.slug === slug);
        setArticle(found || null);
        setTimeseriesData(tsData);
        setDisclosuresData(discData);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [slug]);

  // Get trades for the week, grouped by politician
  const weeklyTrades = useMemo((): PoliticianTrades[] => {
    if (!article || !disclosuresData) return [];

    const weekStart = article.weekStart;
    const weekEnd = article.weekEnd;

    // Filter disclosures within the week
    const weekDisclosures = disclosuresData.disclosures.filter((d) => {
      const pubDate = d.pub_date?.substring(0, 10) || '';
      return pubDate >= weekStart && pubDate <= weekEnd;
    });

    // Group by politician
    const byPolitician: Record<string, Disclosure[]> = {};
    weekDisclosures.forEach((d) => {
      if (!byPolitician[d.politician]) {
        byPolitician[d.politician] = [];
      }
      byPolitician[d.politician].push(d);
    });

    // Build result with politician data
    const result: PoliticianTrades[] = [];
    Object.entries(byPolitician).forEach(([politicianName, trades]) => {
      // Try to find politician in timeseries data
      const politician = timeseriesData?.politicians[politicianName] || null;
      const firstTrade = trades[0];
      const currentValue = politician?.currentValue || 10000;
      const return3Y = ((currentValue - 10000) / 10000) * 100;

      result.push({
        politician,
        politicianName,
        party: politician?.party || firstTrade.party,
        chamber: politician?.chamber || firstTrade.chamber,
        state: politician?.state || firstTrade.state,
        trades,
        return3Y,
        currentValue
      });
    });

    // Sort by 3-year return (best performers first)
    result.sort((a, b) => b.return3Y - a.return3Y);

    return result;
  }, [article, timeseriesData, disclosuresData]);

  const handlePoliticianClick = (item: PoliticianTrades) => {
    navigate(`/politician/${generateSlug(item.politicianName)}`);
  };

  if (loading) {
    return (
      <div className="news-page loading">
        <div className="loader">
          <div className="loader-text">{t('loading')}</div>
          <div className="loader-bar">
            <div className="loader-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="news-page">
        <header className="news-header">
          <div className="news-header-left">
            <h1 className="news-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              {t('appTitle')}
            </h1>
          </div>
        </header>
        <main className="news-main">
          <div className="news-empty">
            <div className="empty-icon">&#128683;</div>
            <p>{t('news:articleNotFound')}</p>
            <button className="back-btn" onClick={() => navigate('/news')}>
              {t('news:backToNews')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="news-page article-page">
      <header className="news-header">
        <div className="news-header-left">
          <h1 className="news-title" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            {t('appTitle')}
          </h1>
          <button className="back-link" onClick={() => navigate('/news')}>
            &larr; {t('news:backToNews')}
          </button>
        </div>
      </header>

      <main className="news-main">
        <article className="article-content">
          <header className="article-header">
            <div className="article-date">
              {formatDate(article.weekStart, 'MMMM d')} - {formatDate(article.weekEnd, 'MMMM d, yyyy')}
            </div>
            <h1 className="article-title">
              {article.title[currentLang] || article.title.en}
            </h1>
          </header>

          <div className="article-highlights">
            <div className="highlight-card top">
              <div className="highlight-label">{t('news:topGainer')}</div>
              <div className="highlight-name">{article.highlights.topGainer.name}</div>
              <div className="highlight-party">
                {article.highlights.topGainer.party === 'Democrat' ? 'D' : 'R'}
              </div>
              <div className="highlight-return positive">
                {formatPercentage(article.highlights.topGainer.return)}
              </div>
            </div>
            <div className="highlight-card bottom">
              <div className="highlight-label">{t('news:topLoser')}</div>
              <div className="highlight-name">{article.highlights.topLoser.name}</div>
              <div className="highlight-party">
                {article.highlights.topLoser.party === 'Democrat' ? 'D' : 'R'}
              </div>
              <div className="highlight-return negative">
                {formatPercentage(article.highlights.topLoser.return)}
              </div>
            </div>
            <div className="highlight-card trades">
              <div className="highlight-label">{t('news:totalTrades')}</div>
              <div className="highlight-value">{article.highlights.totalTrades}</div>
            </div>
          </div>

          {/* Weekly Trades Section */}
          <section className="trades-section">
            <h2 className="trades-section-title">
              {t('news:weeklyTrades')} ({weeklyTrades.reduce((sum, p) => sum + p.trades.length, 0)})
            </h2>
            <p className="trades-section-note">
              {t('news:simulationNote')}
            </p>

            {weeklyTrades.length === 0 ? (
              <p className="no-trades">{t('news:noTradesThisWeek')}</p>
            ) : (
              <div className="trades-by-politician">
                {weeklyTrades.map((item) => (
                  <div key={item.politicianName} className="politician-trades-card">
                    <div
                      className="politician-trades-header"
                      onClick={() => handlePoliticianClick(item)}
                    >
                      <div className="politician-trades-avatar">
                        <img
                          src={`/images/${item.politician?.id || 'placeholder'}.jpg`}
                          alt={item.politicianName}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/placeholder.png';
                          }}
                        />
                      </div>
                      <div className="politician-trades-info">
                        <span className="politician-trades-name">{item.politicianName}</span>
                        <span className="politician-trades-meta">
                          <span className={`party-badge ${item.party === 'Democrat' ? 'dem' : 'rep'}`}>
                            {item.party === 'Democrat' ? 'D' : 'R'}
                          </span>
                          {item.state} · {item.chamber}
                        </span>
                      </div>
                      <div className="politician-trades-return">
                        <span className="return-label">{t('news:portfolioReturn')}</span>
                        <span className={`return-value ${item.return3Y >= 0 ? 'positive' : 'negative'}`}>
                          {formatPercentage(item.return3Y)}
                        </span>
                        <span className="return-portfolio">
                          {formatCurrency(item.currentValue)}
                        </span>
                      </div>
                    </div>
                    <div className="politician-trades-list">
                      {item.trades.map((trade, idx) => (
                        <div key={idx} className={`trade-item ${trade.trade_type.toLowerCase()}`}>
                          <div className="trade-type-badge">
                            {trade.trade_type === 'Buy' ? (
                              <span className="buy">BUY</span>
                            ) : (
                              <span className="sell">SELL</span>
                            )}
                          </div>
                          <div className="trade-details">
                            <span className="trade-ticker">{trade.ticker?.split(':')[0] || trade.issuer}</span>
                            <span className="trade-issuer">{trade.issuer}</span>
                          </div>
                          <div className="trade-meta">
                            <span className="trade-value">{trade.value_range}</span>
                            <span className="trade-owner">{trade.owner}</span>
                          </div>
                          <div className="trade-dates">
                            <div className="trade-date-row">
                              <span className="date-label">{t('news:tradeDate')}</span>
                              <span className="date-value">{trade.trade_date ? formatDate(trade.trade_date, 'MMM d') : '-'}</span>
                            </div>
                            <div className="trade-date-row disclosed">
                              <span className="date-label">{t('news:disclosedDate')}</span>
                              <span className="date-value">{formatDate(trade.pub_date, 'MMM d')}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="article-sidebar">
            <div className="sidebar-section">
              <h4>{t('news:mostTradedStocks')}</h4>
              <div className="stock-list">
                {article.highlights.mostTradedStocks.map((stock, i) => (
                  <span key={i} className="stock-tag">{stock}</span>
                ))}
              </div>
            </div>

            <div className="sidebar-section">
              <h4>{t('news:sectorTrends')}</h4>
              <div className="sector-list">
                {article.highlights.sectorTrends.map((sector, i) => (
                  <div key={i} className={`sector-item ${sector.trend}`}>
                    <span className="sector-icon">
                      {sector.trend === 'bullish' ? '▲' : sector.trend === 'bearish' ? '▼' : '—'}
                    </span>
                    <span className="sector-name">{sector.sector}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <footer className="news-footer">
        <button className="back-to-dashboard" onClick={() => navigate('/')}>
          {t('news:backToDashboard')}
        </button>
      </footer>
    </div>
  );
};

const News: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  if (slug) {
    return <NewsArticlePage />;
  }

  return <NewsListPage />;
};

export default News;
